import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18';

// @mui
import { Checkbox, MenuItem, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import { AppDataContext } from '../../context/Context';
import { getNestedValue, isValidJSON } from '../../utils/formatName';

export default class DialogArrayField extends React.Component {
    key = 'id';
    constructor(props) {
        super(props);
        this.key = props.field.key || 'id';
        this.selectRef = React.createRef();
    }
    static contextType = AppDataContext;

    itemClick(category) {
        let value = this.props.value || [];
        const selected = value.find(
            (cat) => cat[this.key] === category[this.key]
        );
        const newValue = selected
            ? value.filter((cat) => cat[this.key] !== category[this.key])
            : [...value, category];

        // const changedValue = newValue.map((cat) => cat[this.key]);

        this.props.onChange(this.props.field.id, newValue);
    }

    isCategoryChecked(id) {
        let value = this.props.value || [];
        return !!value.find((cat) => cat[this.key] === id);
    }

    getValue = (category, field) => {
        //nested id filed has to be in the format of "nested.id"
        const isNested = field.includes('.');
        const value = isNested
            ? getNestedValue(category, field)
            : category[field];
        const isJSON = isValidJSON(value);
        const parsedValue = isJSON ? JSON.parse(value) : {};
        const validNames = Object.values(parsedValue).filter((name) =>
            name?.trim()
        );
        return validNames.join(' / ') || value;
    };

    getDisplayValue(category) {
        const { primary, secondary } = this.props.field;
        const primaryValue = primary
            ? this.getValue(category, primary)
            : category.name;
        const secondaryValue = secondary
            ? this.getValue(category, secondary)
            : null;
        if (primaryValue && secondaryValue) {
            return `${primaryValue}: ${secondaryValue}`;
        }
        return primaryValue || secondaryValue || 'Unnamed';
    }

    render() {
        if (
            !this.props.field ||
            !this.props.field.label ||
            !this.context.getState
        ) {
            return null;
        }

        const translatedLabel = i18n.t(`columns.${this.props.field.label}`);
        const placeholderLabel = i18n.t(
            `placeholder.${this.props.field.label}`
        );
        const categories =
            this.context.getState().controllersData[this.props.field.from];
        const values = this.props.value || [];

        if (!categories) {
            return null;
        }

        const required = this.props.field.required;
        const requiredLabel = required ? ' *' : '';

        const renderValue = (selected) => {
            if (!selected || selected.length === 0) {
                return placeholderLabel || 'No categories selected';
            }
            return selected
                .map((selectedItem) =>
                    categories.find(
                        (category) =>
                            category[this.key] === selectedItem[this.key]
                    )
                )
                .map((category) => this.getDisplayValue(category))
                .filter(Boolean)
                .join(', ');
        };

        return (
            <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                    {translatedLabel + requiredLabel}
                </Typography>
                <Select
                    id={this.props.field.id}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 200
                            }
                        }
                    }}
                    multiple
                    value={values}
                    renderValue={renderValue}
                    sx={{
                        '& .MuiSelect-select .notranslate::after':
                            placeholderLabel
                                ? {
                                    content: `"${placeholderLabel}"`,
                                    opacity: 0.7
                                }
                                : {}
                    }}
                >
                    {categories.map((category) => {
                        if (!category) return null;

                        const displayValue = this.getDisplayValue(category);

                        return (
                            <MenuItem
                                key={category[this.key]}
                                value={category}
                                onClick={() => this.itemClick(category)}
                            >
                                <Checkbox
                                    checked={this.isCategoryChecked(
                                        category[this.key]
                                    )}
                                />
                                <ListItemText primary={displayValue} />
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
        );
    }
}

DialogArrayField.propTypes = {
    field: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        from: PropTypes.string.isRequired,
        primary: PropTypes.string, // Path to primary value (e.g., "user.name")
        secondary: PropTypes.string, // Path to secondary value
        required: PropTypes.bool
    }).isRequired,
    value: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string
        })
    ),
    onChange: PropTypes.func.isRequired
};

DialogArrayField.defaultProps = {
    value: []
};
